/**
 * Client > Checklists (approved mockup 2026-10-26): guided work for a client. New client setup
 * starts by itself when a client is added as Onboarding, offboarding when it's set to Former,
 * and a renewal 90 days before an agreement's notice date. Steps with an automatic check tick
 * themselves when DocVault sees the work done; the rest are ticked by hand. A step can link to a
 * MyDocUploads form. Accounting ticks only Accounting steps.
 */
import { useEffect, useState } from "react";
import { api, type ChAgreement, type ChClient, type ChRun, type ChTemplate } from "../../lib/api";
import { useStore } from "../../lib/store";
import { fmt } from "../../lib/format";
import { Loading } from "../../components/bits";

const WHO: Record<string, string> = { owner: "Account owner", accounting: "Accounting", admin: "Admin", anyone: "Anyone who edits" };

/** A step's link names the client tab where the work is done. */
const LINK_TAB: Record<string, [string, string]> = { services: ["sv", "Services"], contacts: ["ct", "Contacts"], assets: ["as", "Assets"], networks: ["nw", "Networks"],
  domains: ["dm", "Domains"], agreements: ["ag", "Agreements"], documents: ["dc", "Documents"], overview: ["ov", "Overview"], notes: ["nt", "Notes"] };

export function ChecklistsTab({ c, agreements, go }: { c: ChClient; agreements: ChAgreement[]; go: (tab: string) => void }) {
  const { toast, reloadTasks } = useStore();
  const [data, setData] = useState<Awaited<ReturnType<typeof api.chRuns>> | null>(null);
  const [starting, setStarting] = useState(false);
  const [tpl, setTpl] = useState("");
  const [ag, setAg] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const load = () => api.chRuns(c.id).then(setData, (e: Error) => toast(e.message));
  useEffect(() => { void load(); }, [c.id]);
  if (!data) return <Loading />;
  const perm = data.permissions;
  const open = data.runs.filter((r) => r.status === "open");
  const closed = data.runs.filter((r) => r.status !== "open");
  const templates = data.templates.filter((t: ChTemplate) => t.active);
  const chosen = templates.find((t) => t.id === tpl);
  const run = async (key: string, fn: () => Promise<unknown>, msg: string) => {
    setBusy(key);
    try { await fn(); toast(msg); await load(); void reloadTasks(); return true; } catch (e) { toast((e as Error).message); return false; } finally { setBusy(null); }
  };
  // Same rule as the API: editors tick any step but Admin ones, Accounting ticks Accounting steps, admins tick Admin steps.
  const mayTick = (who: string) => (who === "accounting" ? perm.edit || !!perm.money : who === "admin" ? perm.sensitive : perm.edit);

  const card = (r: ChRun) => {
    const pct = r.total ? Math.round((r.done / r.total) * 100) : 0;
    const overdue = r.due_on && r.status === "open" && r.due_on < new Date().toISOString().slice(0, 10);
    return (
      <div className="card sect" key={r.id} style={{ marginBottom: 14 }}>
        <div className="ck-top">
          <div>
            <h3 style={{ margin: 0 }}>{r.title} {r.due_on && <span className={`pill sm ${overdue ? "bad" : "rev"}`}>Due {fmt(r.due_on).replace(/, \d{4}$/, "")}</span>}</h3>
            <div className="cap">Started {fmt(r.started_at)} · {r.done} of {r.total} done{r.owner ? ` · account owner ${r.owner.name}` : ""}</div>
          </div>
          {perm.edit && <button className="btn btn-ghost btn-sm" disabled={busy === r.id} onClick={() => { if (window.confirm(`Cancel "${r.title}"? Its steps stay in the record.`)) void run(r.id, () => api.chCancelRun(r.id), "Checklist canceled."); }}>Cancel</button>}
        </div>
        <div className="su-bar" style={{ margin: "10px 0" }}><i style={{ width: `${pct}%` }} /></div>
        <ul className="su-list">
          {r.steps.map((s) => {
            const done = !!s.done_at || s.skipped;
            const can = mayTick(s.who) && !s.done_auto;
            return (
              <li key={s.id} className={`ck-row ${done ? "done" : ""}`}>
                <button type="button" className="su-ck" style={{ cursor: can ? "pointer" : "default", background: done ? undefined : "#fff" }} disabled={!can || busy === s.id}
                  aria-label={done ? `Untick: ${s.title}` : `Tick: ${s.title}`} title={!can ? (s.done_auto ? "Ticked automatically" : `For ${WHO[s.who] ?? s.who}`) : undefined}
                  onClick={() => void run(s.id, () => api.chStep(s.id, { done: !done }), done ? "Unticked." : "Done.")}>{done ? "✓" : ""}</button>
                <div><b>{s.title}</b>
                  {s.done_at ? <div className="cap">{s.done_auto ? "Ticked itself" : s.done_by ?? "Done"} · {fmt(s.done_at)}{s.note ? ` · "${s.note}"` : ""}</div>
                    : s.skipped ? <div className="cap">Skipped</div>
                    : s.auto_label ? <div className="cap">Ticks itself when: {s.auto_label.charAt(0).toLowerCase() + s.auto_label.slice(1)}</div>
                    : s.help ? <div className="cap">{s.help}</div> : null}
                  {!done && s.form_url && <div style={{ marginTop: 4 }}><a className="btn btn-ghost btn-sm" href={s.form_url} target="_blank" rel="noreferrer">Open the form in MyDocUploads</a></div>}
                  {!done && s.link && LINK_TAB[s.link] && <div style={{ marginTop: 2 }}><a className="doclink" onClick={() => go(LINK_TAB[s.link]![0])}>Go to {LINK_TAB[s.link]![1]}</a></div>}
                  {!done && s.link && /^https:\/\//.test(s.link) && <div style={{ marginTop: 2 }}><a className="doclink" href={s.link} target="_blank" rel="noreferrer">Open</a></div>}
                </div>
                <span className="cap">{WHO[s.who] ?? s.who}</span>
              </li>
            );
          })}
        </ul>
      </div>
    );
  };

  return (
    <div>
      <div className="ag-head">
        <div className="cap">Guided work for this client. Steps with an automatic check tick themselves.</div>
        {perm.edit && !starting && templates.length > 0 && <button className="btn btn-ghost btn-sm" onClick={() => setStarting(true)}>Start a checklist</button>}
      </div>
      {starting && (
        <div className="card sect" style={{ marginBottom: 14 }}>
          <div className="form-row" style={{ alignItems: "flex-end", margin: 0 }}>
            <label className="grow"><span className="lbl">Checklist</span><select className="inp" value={tpl} onChange={(e) => setTpl(e.target.value)}>
              <option value="">Pick one</option>{templates.map((t) => <option key={t.id} value={t.id}>{t.name} ({t.steps.length} steps)</option>)}</select></label>
            {chosen?.kind === "agreement_renewal" && (
              <label className="grow"><span className="lbl">Agreement</span><select className="inp" value={ag} onChange={(e) => setAg(e.target.value)}>
                <option value="">Pick one</option>{agreements.filter((a) => a.status === "active").map((a) => <option key={a.id} value={a.id}>{a.type_code} {a.title}</option>)}</select></label>
            )}
            <button className="btn btn-accent" disabled={!tpl || (chosen?.kind === "agreement_renewal" && !ag) || busy === "start"}
              onClick={() => void run("start", () => api.chStartRun(c.id, { template_id: tpl, agreement_id: ag || undefined }), "Checklist started.").then((ok) => { if (ok) { setStarting(false); setTpl(""); setAg(""); } })}>Start</button>
            <button className="btn btn-ghost" onClick={() => setStarting(false)}>Cancel</button>
          </div>
        </div>
      )}
      {open.map(card)}
      {!open.length && !starting && <div className="card sect cap" style={{ marginBottom: 14 }}>No checklist in progress. New client setup, offboarding and renewals start by themselves{perm.edit ? "; or start one by hand" : ""}.</div>}
      {closed.length > 0 && (
        <div className="card sect"><h3>Done</h3>
          <ul className="lst">{closed.map((r) => <li key={r.id}><b>{r.title}</b><span className="r cap">{r.status === "cancelled" ? "Canceled" : `${r.done} of ${r.total} · finished ${fmt(r.finished_at)}`}</span></li>)}</ul></div>
      )}
    </div>
  );
}
